import { useState, type ReactNode } from 'react';
import { LoadFailed } from '../components/QueryState';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canAuthorAutomations } from '@promanage/core';
import { Button, Chip, Input, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useSession } from '../features/auth/session';
import { RaisedWork } from '../features/automations/RaisedWork';
import { RunLog } from '../features/automations/RunLog';
import { activityText, agoText, ruleState, whenText } from '../features/automations/format';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/automations/')({
  component: AutomationsPage,
});

/** Past this many rules the list is long enough to be worth searching. */
const SEARCH_FROM = 6;

/**
 * Automations — M5. The agency's own rules over the event stream, and what they have done.
 *
 * WHAT THIS SCREEN IS FOR is one question asked in three ways: is my automation working? What is
 * switched on, whether it has fired, and whether anything went wrong — so the health figures sit
 * in the top bar, the rules and the run log share the main column, and what the rules have RAISED sits
 * beside them, where the day's work is visible without scrolling past a record of the past.
 *
 * A RULE THAT HAS NOT FIRED CAN MEAN THREE THINGS — nothing matched it, the agency paused
 * everything, or the worker is behind — and only the third is an emergency. All three are in the
 * figures, including the drain's backlog, so the third case never looks like the first.
 *
 * QUIET UNTIL SOMETHING IS WRONG. Each figure, and each rule, says only what it is when all is
 * well; the sentence of explanation appears when there is something to explain. A page that
 * explained itself on every row was a page nobody could scan for the one row that mattered.
 *
 * SUGGESTIONS OPEN THE ORDINARY FORM FILLED IN, never create anything on click: what gets saved is
 * an agency-owned rule they read before it acted, and can edit, disable or delete.
 */
function AutomationsPage() {
  const session = useSession();
  const mayAuthor = canAuthorAutomations(session?.roles ?? []);
  const overview = useQuery(
    trpc.automation.overview.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load this agency’s automations' },
      staleTime: 10_000,
    }),
  );

  const data = overview.data;
  const rules = data?.rules ?? [];
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const visibleRules = rules.filter((rule) =>
    `${rule.name} ${rule.sentence}`.toLowerCase().includes(needle),
  );
  const now = new Date();

  const subtitle = overview.isPending
    ? 'Loading…'
    : data === undefined
      ? 'Automations'
      : data.ruleCount === 0
        ? 'Nothing automated yet'
        : `${String(data.ruleCount)} ${data.ruleCount === 1 ? 'automation' : 'automations'} · ${String(data.enabledCount)} switched on`;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Automations"
        subtitle={subtitle}
        meta={data ? <Health data={data} /> : undefined}
        actions={
          mayAuthor ? (
            <>
              {data ? <KillSwitch paused={data.paused} /> : null}
              <Link to="/automations/new">
                <Button size="sm">
                  <span aria-hidden="true">+</span> Add automation
                </Button>
              </Link>
            </>
          ) : null
        }
      />

      <div className="flex flex-col gap-4">
        {overview.isError ? (
          <Panel className="px-5 py-4">
            <LoadFailed
              inline
              retry={() => void overview.refetch()}
              retrying={overview.isFetching}
            />
          </Panel>
        ) : null}

        {data?.paused ? (
          <Panel className="border-l-4 border-warn px-5 py-4">
            <p className="text-[0.9375rem] font-semibold text-warn">All automations are stopped</p>
            {/*
             * SAYING WHAT RESTART DOES, because it is the question somebody hesitates over. The
             * backlog does not replay — rules resume on new events — which is what prevents the
             * "every rule fires once per event since 2026" disaster §4 describes.
             */}
            <p className="mt-1 text-[0.8125rem] text-ink-muted">
              By {data.pausedByName ?? 'somebody'} at {whenText(data.pausedAt)}. Events are still
              recorded; on restart, rules act on new ones only.
            </p>
          </Panel>
        ) : null}

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <div className="flex min-w-0 flex-col gap-4">
            <Panel>
              <PanelHeader
                title="Your automations"
                subtitle={
                  data && data.ruleCount > 0 ? `Last ${String(data.activityDays)} days` : undefined
                }
              />

              {rules.length >= SEARCH_FROM ? (
                <div className="px-5 pb-3">
                  <Input
                    aria-label="Search automations"
                    placeholder="Search names, triggers or actions…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
              ) : null}

              <div className="border-t border-rule">
                {overview.isPending ? (
                  <p className="px-5 py-4 text-[0.8125rem] text-ink-muted">Loading…</p>
                ) : overview.isError && !data ? null : rules.length === 0 ? (
                  <div className="px-5 py-6">
                    <p className="text-[0.9375rem] font-bold">No automations yet</p>
                    <p className="mt-1 text-[0.8125rem] text-ink-muted">
                      {mayAuthor
                        ? 'Start from a suggestion, or write your own.'
                        : 'An administrator can set them up.'}
                    </p>
                    {mayAuthor ? (
                      <Link to="/automations/new" className="mt-3 inline-block">
                        <Button size="sm">Add automation</Button>
                      </Link>
                    ) : null}
                  </div>
                ) : visibleRules.length === 0 ? (
                  <div className="px-5 py-4">
                    <p className="text-[0.8125rem] text-ink-muted">
                      Nothing matches “{search.trim()}”.{' '}
                      <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="font-semibold text-accent hover:underline"
                      >
                        Clear search
                      </button>
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-rule">
                    {visibleRules.map((rule) => {
                      const state = ruleState(rule, data?.paused ?? false);
                      /*
                       * ONE WARNING LINE, and only when it is this rule's own problem. Paused-with-
                       * everything is said once, in the banner above; repeating it on every row is
                       * how the one rule that stopped itself gets lost.
                       */
                      const warning =
                        rule.suspendedAt !== null
                          ? state.detail
                          : rule.enabled && !rule.runnable
                            ? rule.because
                            : null;
                      return (
                        <li key={rule.id}>
                          <Link
                            to="/automations/$id"
                            params={{ id: rule.id }}
                            className="group flex flex-col gap-1 px-5 py-3 transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
                          >
                            <div className="flex items-center gap-3">
                              <span className="min-w-0 flex-1 truncate text-[0.9375rem] font-semibold group-hover:text-accent">
                                {rule.name}
                              </span>
                              <Chip tone={state.tone}>{state.label}</Chip>
                            </div>
                            <p className="line-clamp-2 text-[0.8125rem] text-ink-muted">
                              {rule.sentence}
                            </p>
                            <p className="text-[0.75rem] tabular-nums text-ink-subtle">
                              {activityText(rule)} · {agoText(rule.lastRunAt, now).toLowerCase()}
                            </p>
                            {warning ? (
                              <p
                                className={
                                  rule.suspendedAt !== null
                                    ? 'text-[0.75rem] text-danger'
                                    : 'text-[0.75rem] text-warn'
                                }
                              >
                                {warning}
                              </p>
                            ) : null}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </Panel>

            <RunLog />
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <RaisedWork />
            {mayAuthor && data ? <Suggestions /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The three numbers that say whether automation is alive, in the top bar beside the title — the
 * same place and the same figure style as Reconcile's trust position. They were a row of cards,
 * three wide boxes each holding one short number.
 *
 * THE BACKLOG IS THE ONE NOBODY WOULD THINK TO PUT HERE, and it is the one that matters at 9am on
 * a Monday: a drain that is hours behind looks, from every rule's point of view, exactly like an
 * agency where nothing has happened.
 *
 * Colour carries the warning; the words only appear when there is something to act on.
 */
function Health({
  data,
}: {
  data: {
    enabledCount: number;
    suspendedCount: number;
    actionsThisHour: number;
    rateCeiling: number;
    undelivered: number;
    oldestUndeliveredAt: string | null;
  };
}) {
  const nearCeiling = data.actionsThisHour >= data.rateCeiling * 0.8;

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <Figure label="Switched on" value={String(data.enabledCount)}>
        {data.suspendedCount > 0 ? (
          <span className="text-danger" title="Stopped themselves after repeated failures">
            {' '}
            · {String(data.suspendedCount)} failing
          </span>
        ) : null}
      </Figure>
      <Figure
        label="Actions this hour"
        value={`${String(data.actionsThisHour)} / ${String(data.rateCeiling)}`}
        tone={nearCeiling ? 'warn' : 'neutral'}
        // Above the ceiling runs are held and shown, never dropped.
        {...(nearCeiling ? { title: 'Near the hourly limit — extra runs will be held' } : {})}
      />
      <Figure
        label="Events waiting"
        value={String(data.undelivered)}
        tone={data.undelivered > 100 ? 'warn' : 'neutral'}
        {...(data.undelivered > 0
          ? { title: `Oldest recorded ${whenText(data.oldestUndeliveredAt)}` }
          : {})}
      />
    </div>
  );
}

function Figure({
  label,
  value,
  tone = 'neutral',
  title,
  children,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warn';
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className="leading-tight whitespace-nowrap" title={title}>
      <span className="block text-[0.6875rem] text-ink-subtle">{label}</span>
      <span
        className={
          tone === 'warn'
            ? 'text-[0.9375rem] font-semibold tabular-nums text-warn'
            : 'text-[0.9375rem] font-semibold tabular-nums'
        }
      >
        {value}
      </span>
      {children}
    </div>
  );
}

/** §7's switch. One control, and it does not touch any rule's own setting. */
function KillSwitch({ paused }: { paused: boolean }) {
  const queryClient = useQueryClient();

  const set = useMutation(
    trpc.automation.setPaused.mutationOptions({
      meta: { errorTitle: 'Could not change the agency’s automation status' },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.automation.overview.queryKey() });
      },
    }),
  );

  return (
    <Button
      size="sm"
      variant={paused ? 'primary' : 'secondary'}
      disabled={set.isPending}
      onClick={() => set.mutate({ paused: !paused })}
    >
      {set.isPending ? 'Working' : paused ? 'Restart automations' : 'Stop all automations'}
    </Button>
  );
}

/**
 * Rules we suggest, which the agency then owns.
 *
 * EACH ONE LINKS TO THE ORDINARY AUTHORING FORM with the fields filled in, rather than creating
 * anything on click. A rule an agency did not read before it started emailing their owners is
 * exactly the vendor-decided automation this milestone exists to beat.
 *
 * THE NAME ONLY. Each is written to say what it does ("Chase a notice to remedy"), and the form it
 * opens reads the whole rule back as a sentence before anything is saved — so the list does not
 * have to, and seven two-line descriptions stacked in a side column were a wall of text.
 */
function Suggestions() {
  const catalogue = useQuery(
    trpc.automation.catalogue.queryOptions(undefined, { staleTime: 60 * 60_000 }),
  );

  const templates = catalogue.data?.templates ?? [];
  if (templates.length === 0) return null;

  return (
    <Panel>
      <PanelHeader title="Suggestions" />
      <ul className="divide-y divide-rule border-t border-rule">
        {templates.map((template) => (
          <li key={template.id}>
            <Link
              to="/automations/new"
              search={{ template: template.id }}
              // The full rule, for anybody who hovers; the form spells it out once chosen.
              title={template.sentence}
              className="group flex items-center gap-3 px-5 py-2.5 transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
            >
              <span className="min-w-0 flex-1 text-[0.8125rem] font-medium group-hover:text-accent">
                {template.name}
              </span>
              <span
                aria-hidden="true"
                className="shrink-0 text-[0.8125rem] text-ink-subtle group-hover:text-accent"
              >
                +
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
